import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 10;

const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const safeLower = (v) => safeText(v).toLowerCase();

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

const pickCategory = (p) => p?.category?.name || p?.categoryName || p?.category || "-";
const pickSubcategory = (p) => p?.subcategory?.name || p?.subcategoryName || p?.subcategory || "-";

const pickSeller = (p) => p?.seller || p?.sellerId || p?.createdBy || p?.userId || null;
const pickSellerId = (p) => {
  const s = pickSeller(p);
  if (!s) return null;
  if (typeof s === "string") return s;
  return s?._id || s?.id || null;
};
const pickSellerName = (p) => {
  const s = pickSeller(p);
  if (!s) return "-";
  if (typeof s === "string") return "Seller";
  return s?.name || s?.fullName || s?.companyName || s?.shopName || "-";
};

const SaleByOGCSPage = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  // Pagination
  const [page, setPage] = useState(1);

  // ✅ Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${baseurl}/api/product`);

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.products)
          ? response.data.products
          : [];

        const ogcsProducts = list.filter((p) => p?.saleType === "Sale By OGCS");
        setProducts(ogcsProducts);
      } catch (err) {
        console.error("Error fetching OGCS products:", err);
        setError(err.response?.data?.message || "Failed to fetch OGCS products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // ✅ Search + sort
  const filtered = useMemo(() => {
    let list = [...products];

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        return (
          safeLower(p?.name).includes(q) ||
          safeLower(pickCategory(p)).includes(q) ||
          safeLower(pickSubcategory(p)).includes(q) ||
          safeLower(pickSellerName(p)).includes(q) ||
          safeLower(p?.visibilityLevel).includes(q) ||
          safeLower(p?.saleType).includes(q)
        );
      });
    }

    const byDateDesc = (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
    const byDateAsc = (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0);
    const byPriceAsc = (a, b) => Number(a?.price || 0) - Number(b?.price || 0);
    const byPriceDesc = (a, b) => Number(b?.price || 0) - Number(a?.price || 0);
    const byNameAsc = (a, b) => safeText(a?.name).localeCompare(safeText(b?.name));
    const byNameDesc = (a, b) => safeText(b?.name).localeCompare(safeText(a?.name));

    if (sortOrder === "newest") list.sort(byDateDesc);
    if (sortOrder === "oldest") list.sort(byDateAsc);
    if (sortOrder === "price-asc") list.sort(byPriceAsc);
    if (sortOrder === "price-desc") list.sort(byPriceDesc);
    if (sortOrder === "name-asc") list.sort(byNameAsc);
    if (sortOrder === "name-desc") list.sort(byNameDesc);

    return list;
  }, [products, search, sortOrder]);

  useEffect(() => setPage(1), [search, sortOrder]);

  // ✅ Pagination
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ✅ FIX: use the WORKING endpoint (same as your ProductDetails page)
  const handleViewDetailsModal = async (productId) => {
    try {
      setDetailsLoading(true);
      setError("");

      const res = await axios.get(`${baseurl}/api/seller/details/${productId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      setSelectedProduct(res.data);
    } catch (err) {
      console.error("Error fetching product details:", err);
      setError(err.response?.data?.message || "Error fetching product details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const goOgcsDetailsPage = (id) => id && navigate(`/ogcs-product-details/${id}`);
  const goProductDetails = (id) => id && navigate(`/product/${id}`);

  const closeModal = () => setSelectedProduct(null);

  useEffect(() => {
    const onKeyDown = (e) => e.key === "Escape" && closeModal();
    if (selectedProduct) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedProduct]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading OGCS products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Sale By OGCS</h1>
            <p className="text-sm text-gray-600 mt-1">
              OGCS-managed products list with search, sorting and pagination.
            </p>
          </div>

          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Total: <b>{products.length}</b> • Showing: <b>{filtered.length}</b>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Product / Category / Seller / Visibility..."
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500">Sort</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-asc">Price Low → High</option>
                <option value="price-desc">Price High → Low</option>
                <option value="name-asc">Name A → Z</option>
                <option value="name-desc">Name Z → A</option>
              </select>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={() => setSearch("")}
                className="w-full px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                Clear
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Product</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3 text-left font-semibold">Seller</th>
                <th className="px-4 py-3 text-left font-semibold">Price</th>
                <th className="px-4 py-3 text-left font-semibold">Stock</th>
                <th className="px-4 py-3 text-left font-semibold">Visibility</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    No OGCS products found.
                  </td>
                </tr>
              ) : (
                pageItems.map((p) => (
                  <tr key={p._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{safeText(p.name)}</div>
                      <div className="text-xs text-gray-500 font-mono break-all">{safeText(p._id)}</div>
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      <div className="text-gray-900">{pickCategory(p)}</div>
                      <div className="text-xs text-gray-500">{pickSubcategory(p)}</div>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{pickSellerName(p)}</td>

                    <td className="px-4 py-3 text-gray-700">
                      ₹{inr(p.price)}{" "}
                      <span className="text-xs text-gray-500">
                        {p.priceUnit ? `(${p.priceUnit})` : ""}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{safeText(p.stock)}</td>

                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                        {safeText(p.visibilityLevel || "N/A")}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => handleViewDetailsModal(p._id)}
                          className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                          View
                        </button>

                        <button
                          onClick={() => goOgcsDetailsPage(p._id)}
                          className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                          OGCS Details
                        </button>

                        <button
                          onClick={() => goProductDetails(p._id)}
                          className="px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
                        >
                          Product Page
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Page <b>{page}</b> of <b>{totalPages}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div
            className="bg-white w-full max-w-4xl rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{safeText(selectedProduct.name)}</h2>
                <div className="text-xs text-gray-500 mt-1">
                  {pickCategory(selectedProduct)}
                  {pickSubcategory(selectedProduct) !== "-" ? ` • ${pickSubcategory(selectedProduct)}` : ""}
                </div>
              </div>

              <button onClick={closeModal} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">
                ✕
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              {detailsLoading ? (
                <div className="text-sm text-gray-600">Loading details…</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <InfoCard
                      label="Price"
                      value={`₹${inr(selectedProduct.price)} ${
                        selectedProduct.priceUnit ? `(${selectedProduct.priceUnit})` : ""
                      }`}
                    />
                    <InfoCard label="Stock" value={safeText(selectedProduct.stock)} />
                    <InfoCard label="Visibility" value={safeText(selectedProduct.visibilityLevel || "N/A")} />
                    <InfoCard label="Created At" value={dtIN(selectedProduct.createdAt)} />
                    <InfoCard label="HSN" value={safeText(selectedProduct.hsnCode)} />
                    <InfoCard label="Seller" value={pickSellerName(selectedProduct)} />
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-500">Description</div>
                    <div className="text-sm text-gray-800 mt-1 leading-relaxed">
                      {selectedProduct.description || "No description available."}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-5 border-t flex justify-end gap-2 flex-wrap">
              <button
                onClick={() => goOgcsDetailsPage(selectedProduct._id)}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Open OGCS Details
              </button>

              <button
                onClick={() => goProductDetails(selectedProduct._id)}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
              >
                Open Product Page
              </button>

              <button onClick={closeModal} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold text-gray-900 mt-1 break-all">{safeText(value)}</div>
  </div>
);

export default SaleByOGCSPage;



























// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { baseurl } from "../../config/url";

// const SaleByOGCSPage = () => {
//   const [products, setProducts] = useState([]);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [showSidebar, setShowSidebar] = useState(false);

//   // Fetch products with saleType "Sale By OGCS"
//   useEffect(() => {
//     const fetchProducts = async () => {
//       try {
//         const response = await axios.get(`${baseurl}/api/product`);
//         console.log("API Response:", response.data);

//         const data = Array.isArray(response.data) ? response.data : response.data.products;
//         const ogcsProducts = data.filter((product) => product.saleType === "Sale By OGCS");

//         setProducts(ogcsProducts);
//       } catch (error) {
//         console.error("Error fetching OGCS products:", error);
//       }
//     };

//     fetchProducts();
//   }, []);

//   const handleActionClick = async (productId) => {
//     try {
//       const response = await axios.get(`${baseurl}/api/seller/product-details/${productId}`);
//       console.log("Product Details:", response.data);
//       setSelectedProduct(response.data);
//       setShowSidebar(true);
//     } catch (error) {
//       console.error("Error fetching product details:", error);
//     }
//   };

//   const closeSidebar = () => {
//     setShowSidebar(false);
//     setSelectedProduct(null);
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-r from-green-50 via-white to-green-100 p-8">
//       <h1 className="text-4xl font-bold text-green-600 text-center mb-8 shadow-sm">
//         Sale By OGCS
//       </h1>
//       <div className="relative flex">
//         {/* Product List */}
//         <div className={`flex-1 bg-white shadow-lg rounded-lg p-6 ${showSidebar ? "mr-96" : ""}`}>
//           <table className="table-auto w-full text-left border-collapse border border-gray-300 rounded-lg">
//             <thead>
//               <tr className="bg-green-200 text-gray-700">
//                 <th className="p-4 border border-gray-300 font-medium text-lg">Product Name</th>
//                 <th className="p-4 border border-gray-300 font-medium text-lg">Price</th>
//                 <th className="p-4 border border-gray-300 font-medium text-lg">Stock</th>
//                 <th className="p-4 border border-gray-300 font-medium text-lg">Visibility Level</th>
//                 <th className="p-4 border border-gray-300 font-medium text-lg">Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {products.map((product) => (
//                 <tr key={product._id} className="hover:bg-green-50 transition duration-300">
//                   <td className="p-4 border border-gray-300">{product.name}</td>
//                   <td className="p-4 border border-gray-300">{product.price}</td>
//                   <td className="p-4 border border-gray-300">{product.stock}</td>
//                   <td className="p-4 border border-gray-300">
//                     {product.visibilityLevel || "N/A"}
//                   </td>
//                   <td className="p-4 border border-gray-300">
//                     <button
//                       onClick={() => handleActionClick(product._id)}
//                       className="bg-green-500 text-white px-4 py-2 rounded shadow-md hover:bg-green-600 transition"
//                     >
//                       View Details
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//      {/* Sidebar */}
// {showSidebar && selectedProduct && (
//   <div className="fixed right-0 top-0 h-full w-96 bg-gradient-to-r from-white to-green-50 shadow-2xl p-6 overflow-y-auto z-50">
//     <button
//       className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl"
//       onClick={closeSidebar}
//     >
//       &times;
//     </button>
//     <h2 className="text-3xl font-bold text-green-700 mb-6 border-b pb-2">
//        {selectedProduct.name}
//     </h2>
//     <div className="space-y-6 text-sm">
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Description:</span> {selectedProduct.description || "No description available"}
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Price:</span> ₹{selectedProduct.price} <span className="text-sm text-gray-500">({selectedProduct.priceUnit || "per unit"})</span>
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Available Stock:</span> {selectedProduct.stock || "Out of stock"}
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Category:</span> {selectedProduct.category?.name || "Uncategorized"}
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Subcategory:</span> {selectedProduct.subcategory?.name || "Not specified"}
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Size:</span> {selectedProduct.size || "Standard"}
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">Sold Units:</span> {selectedProduct.sold || 0}
//       </p>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">HSN Code:</span> {selectedProduct.hsnCode || "Not available"}
//       </p>
//       <div>
//         <p className="font-semibold text-green-600">Key Features:</p>
//         <ul className="list-disc list-inside text-gray-700">
//           {selectedProduct.specifications?.length > 0 ? (
//             selectedProduct.specifications.map((spec, index) => (
//               <li key={index}>
//                 <span className="font-semibold">{spec.key}:</span> {spec.value}
//               </li>
//             ))
//           ) : (
//             <li>No specifications listed</li>
//           )}
//         </ul>
//       </div>
//       <div>
//         <p className="font-semibold text-green-600">🖼️ Product Images:</p>
//         <div className="grid grid-cols-1 gap-2">
//           {selectedProduct.images?.length > 0 ? (
//             selectedProduct.images.map((image, index) => (
//               <div key={index} className="w-full h-40 bg-gray-100 rounded-lg shadow-md overflow-hidden">
//                 <img
//                   src={`data:${image.contentType};base64,${image.data}`}
//                   alt={`Product Image ${index + 1}`}
//                   className="w-full h-full object-cover"
//                 />
//               </div>
//             ))
//           ) : (
//             <p className="text-gray-500">No images available</p>
//           )}
//         </div>
//       </div>
//       <p className="text-gray-700">
//         <span className="font-semibold text-green-600">👨‍💼 Seller Information:</span> {selectedProduct.seller?.name || "Not available"}
//       </p>
//     </div>
//   </div>
// )}

//     </div>
//   </div>
//   );
// };

// export default SaleByOGCSPage;
